import { Fragment, useEffect, useRef, useState } from 'react';
import { format, isSameDay, startOfDay } from 'date-fns';
import { DATE_FORMAT_ISO_DAY } from '../../../constants/dateFormat';
import { HOUR_SLOTS, publicationDayKey, publicationSlotIndex } from '../functions';
import { EMPTY_PUBLICATIONS } from '../calendarCellAnchor';
import type { CalendarDayLabel } from '../calendarTemporalLabels';
import {
    CalendarGranularity,
    type CalendarPublication,
    type CalendarPublicationHoverAnchor,
} from '../types';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { WeekDayHeader } from './WeekDayHeader';
import { HourColumn } from './HourColumn';
import { TimeSlotCell } from './TimeSlotCell';

const TIME_SLOT_ROW_MIN_PX = 156;
const TIME_SLOT_ROW_MIN_CLASS = 'min-h-[156px]';
const MAX_VISIBLE_HOUR_ROWS = 4;
const WEEK_DAY_BODY_SCROLL_MAX_HEIGHT_PX = MAX_VISIBLE_HOUR_ROWS * TIME_SLOT_ROW_MIN_PX;
const CURRENT_TIME_TICK_INTERVAL_MS = 60_000;
const MINUTES_PER_HOUR = 60;

const CONTAINER_BASE_CLASS = 'rounded-md border-r border-light-gray bg-white';
const HEADER_ROW_CLASS = 'flex items-start overflow-y-hidden bg-primary-light';
const HEADER_SPACER_CLASS = 'w-16 self-stretch bg-white';
const HEADER_DAYS_CLASS = 'flex-1 grid';
const BODY_GRID_CLASS =
    'grid flex-1 overflow-hidden rounded-bl-md border-b border-l border-light-gray';

function getCurrentTimeOffsetPx(now: Date): number {
    const fractionalHours = now.getHours() + now.getMinutes() / MINUTES_PER_HOUR;
    return fractionalHours * TIME_SLOT_ROW_MIN_PX;
}

function groupPublicationsBySlot(
    publications: readonly CalendarPublication[],
    days: readonly Date[],
): ReadonlyMap<string, readonly CalendarPublication[]> {
    const dayKeys = new Set(days.map(day => format(startOfDay(day), DATE_FORMAT_ISO_DAY)));
    const cells = new Map<string, CalendarPublication[]>();
    for (const publication of publications) {
        const dayKey = publicationDayKey(publication.publicationDate);
        if (!dayKeys.has(dayKey)) {
            continue;
        }
        const cellKey = `${dayKey}-${publicationSlotIndex(publication.publicationDate)}`;
        const existing = cells.get(cellKey);
        if (existing) {
            existing.push(publication);
        } else {
            cells.set(cellKey, [publication]);
        }
    }
    return cells;
}

type WeekDayViewProps = Readonly<{
    mode: Extract<CalendarGranularity, CalendarGranularity.Week | CalendarGranularity.Day>;
    days: readonly Date[];
    publications: readonly CalendarPublication[];
    dayLabels: Readonly<Record<string, CalendarDayLabel>>;
    onPublicationHoverStart: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
    onPublicationHoverEnd: () => void;
    onOpenDayList: (day: Date, top: number, left: number) => void;
}>;

export function WeekDayView({
    mode,
    days,
    publications,
    dayLabels,
    onPublicationHoverStart,
    onPublicationHoverEnd,
    onOpenDayList,
}: WeekDayViewProps) {
    const isWeek = mode === CalendarGranularity.Week;
    const dayKeys = days.map(day => format(startOfDay(day), DATE_FORMAT_ISO_DAY));
    const publicationsBySlot = groupPublicationsBySlot(publications, days);
    const gridTemplateColumns = `repeat(${days.length}, minmax(0, 1fr))`;

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [now, setNow] = useState<Date>(() => new Date());

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), CURRENT_TIME_TICK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        const offsetPx = getCurrentTimeOffsetPx(new Date());
        container.scrollTop = Math.max(0, offsetPx - WEEK_DAY_BODY_SCROLL_MAX_HEIGHT_PX / 2);
    }, []);

    const todayIsVisible = days.some(day => isSameDay(day, now));
    const currentTimeOffsetPx = getCurrentTimeOffsetPx(now);

    return (
        <div className={`${CONTAINER_BASE_CLASS}${isWeek ? ' min-w-0' : ''}`}>
            <div className={`w-full ${isWeek ? 'min-w-[576px]' : 'min-w-0'}`}>
                <div className={HEADER_ROW_CLASS} style={{ scrollbarGutter: 'stable' }}>
                    <div className={HEADER_SPACER_CLASS} aria-hidden />
                    <div className={HEADER_DAYS_CLASS} style={{ gridTemplateColumns }}>
                        {dayKeys.map(dayKey => (
                            <WeekDayHeader key={dayKey} mode={mode} dayLabel={dayLabels[dayKey]} />
                        ))}
                    </div>
                </div>
                <div
                    ref={scrollContainerRef}
                    className="overflow-y-auto"
                    style={{
                        maxHeight: WEEK_DAY_BODY_SCROLL_MAX_HEIGHT_PX,
                        scrollbarGutter: 'stable',
                    }}
                >
                    <div className="relative flex items-start">
                        <HourColumn rowMinClass={TIME_SLOT_ROW_MIN_CLASS} />
                        <div className={BODY_GRID_CLASS} style={{ gridTemplateColumns }}>
                            {HOUR_SLOTS.map((slot, slotIndex) => (
                                <Fragment key={slot.label}>
                                    {days.map((day, dayIndex) => {
                                        const dayKey = dayKeys[dayIndex];
                                        const cellKey = `${dayKey}-${slotIndex}`;
                                        return (
                                            <TimeSlotCell
                                                key={cellKey}
                                                day={day}
                                                dayKey={dayKey}
                                                slot={slot}
                                                rowMinClass={TIME_SLOT_ROW_MIN_CLASS}
                                                isLastRow={slotIndex === HOUR_SLOTS.length - 1}
                                                isLastColumn={dayIndex === days.length - 1}
                                                publications={
                                                    publicationsBySlot.get(cellKey) ??
                                                    EMPTY_PUBLICATIONS
                                                }
                                                inlineCardVariant={mode}
                                                now={now}
                                                onPublicationHoverStart={onPublicationHoverStart}
                                                onPublicationHoverEnd={onPublicationHoverEnd}
                                                onOpenDayList={onOpenDayList}
                                            />
                                        );
                                    })}
                                </Fragment>
                            ))}
                        </div>
                        {todayIsVisible ? (
                            <CurrentTimeIndicator offsetPx={currentTimeOffsetPx} />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
